'use strict';

const makeFoil = require('./visual/foil-paint.js');

const P = require('./protocol.js');

const ranks = {
  none: 0,
  foil: 1,
  crystal: 2,
  thunder: 3,
  flame: 4,
  rift: 5,
  galaxy: 6,
  celestial: 7,
  mythic3: 8,
  mythic2: 9,
  mythic1: 10
}; // Imports are supplied after mount (never during Gatsby SSR); approved vendor bodies retain their own lifecycle.

function createVisuals({
  host,
  ensureVendors
}) {
  async function mount({
    canvas,
    fxCanvas,
    audioButton,
    audioStatus,
    reduceControl,
    resolveOutcome,
    onBegin,
    onComplete,
    onError = () => {}
  }) {
    var _host$visualViewport;

    await ensureVendors();
    const doc = host.document,
          ctx = canvas.getContext('2d'),
          CARD = {
      x: 87,
      y: 73,
      w: 306,
      h: 438
    },
          W = 480,
          H = 610;
    P.check(ctx && host.OPPeelGesture && host.OPCardRenderer && host.OPRevealFX && host.OPOpeningAudio && host.OPCardAssets, 'VISUAL_MODULES_NOT_READY');
    let disposed = false,
        frame = 0,
        snapshot = null,
        visual = null,
        image = null,
        assetGeneration = 0,
        pending = false,
        played = false,
        tiltX = 0,
        tiltY = 0,
        pointer = null,
        contact = null,
        assetPromise = null,
        revealAt = null;
    const pointerSupported = typeof host.PointerEvent === 'function' && typeof canvas.setPointerCapture === 'function';

    const motion = host.matchMedia('(prefers-reduced-motion: reduce)'),
          reduced = () => motion.matches || reduceControl.checked;

    const gesture = host.OPPeelGesture.createController({
      rect: CARD,
      deadzone: 8,
      hitSlop: 20
    });
    const fx = host.OPRevealFX.create({
      canvas: fxCanvas,
      onComplete: () => {}
    }),
          audio = host.OPOpeningAudio.create({
      host,
      document: doc,
      buttons: [audioButton],
      status: audioStatus
    });
    const assets = host.OPCardAssets.create({
      maxEntries: 5,
      maxPixels: 8000000
    });

    function cardFront() {
      var _snapshot, _snapshot$pack, _snapshot2, _snapshot2$pack;

      if (visual && image) host.OPCardRenderer.draw(ctx, visual.card, visual.style, image, CARD, {
        parallax: ((_snapshot = snapshot) === null || _snapshot === void 0 ? void 0 : (_snapshot$pack = _snapshot.pack) === null || _snapshot$pack === void 0 ? void 0 : _snapshot$pack.status) === 'OPENED' && !reduced(),
        tiltX,
        tiltY,
        glare: ((_snapshot2 = snapshot) === null || _snapshot2 === void 0 ? void 0 : (_snapshot2$pack = _snapshot2.pack) === null || _snapshot2$pack === void 0 ? void 0 : _snapshot2$pack.status) === 'OPENED'
      });
    }

    const paint = makeFoil({
      ctx,
      CARD,
      cardFront,
      peelDirection: () => gesture.snapshot().direction
    });

    function actualRect() {
      const r = canvas.getBoundingClientRect();
      return {
        x: r.left + CARD.x / W * r.width,
        y: r.top + CARD.y / H * r.height,
        width: CARD.w / W * r.width,
        height: CARD.h / H * r.height
      };
    }

    function requestDraw() {
      if (!disposed && !doc.hidden && !frame) frame = host.requestAnimationFrame(draw);
    }

    function draw(now) {
      var _snapshot3, _snapshot3$pack;

      frame = 0;
      if (disposed || doc.hidden) return;
      ctx.setTransform(canvas.width / W, 0, 0, canvas.height / H, 0, 0);
      ctx.clearRect(0, 0, W, H);

      if (!image || !visual) {
        paint.cardBack();
        paint.peelHint();
        return;
      }

      if (((_snapshot3 = snapshot) === null || _snapshot3 === void 0 ? void 0 : (_snapshot3$pack = _snapshot3.pack) === null || _snapshot3$pack === void 0 ? void 0 : _snapshot3$pack.status) === 'OPENED') {
        const t = revealAt === null ? 9 : (now - revealAt) / 1000,
              lift = reduced() ? 0 : t < .3 ? -Math.sin(Math.PI * t / .3) * .009 : t < 1.8 ? Math.sin(Math.PI * (t - .3) / 1.5) * .048 : 0;
        ctx.save();
        ctx.translate(240, 290 - lift * 210);
        ctx.scale(1 + lift, 1 + lift);
        ctx.rotate(tiltX * .021);
        ctx.translate(-240, -290);
        cardFront();
        ctx.restore();
        if (t < 1.8) requestDraw();
      } else paint.squeezedCard(Math.min(.985, gesture.snapshot().progress));
    }

    function resize() {
      if (disposed) return;

      if (pointer !== null) {
        gesture.cancel(pointer);
        pointer = null;
      }

      contact = null;
      const dpr = Math.min(host.devicePixelRatio || 1, 2);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      fx.resize();
      requestDraw();
    }

    function point(e) {
      const r = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - r.left) / r.width * W,
        y: (e.clientY - r.top) / r.height * H
      };
    }

    function showEffect() {
      var _snapshot4, _snapshot4$pack;

      if (disposed || !image || ((_snapshot4 = snapshot) === null || _snapshot4 === void 0 ? void 0 : (_snapshot4$pack = _snapshot4.pack) === null || _snapshot4$pack === void 0 ? void 0 : _snapshot4$pack.status) !== 'OPENED' || doc.hidden) return;

      const start = () => {
        if (disposed || doc.hidden) return;
        revealAt = host.performance.now();
        if (visual.card.effect !== 'none') fx.play({
          effect: visual.card.effect,
          reducedMotion: reduced(),
          rect: actualRect()
        });
        audio.reveal(ranks[visual.card.effect]);
        requestDraw();
      };

      const r = actualRect();

      if (r.x < 8 || r.y < 8 || r.x + r.width > host.innerWidth - 8 || r.y + r.height > host.innerHeight - 8) {
        canvas.scrollIntoView({
          behavior: 'instant',
          block: 'center',
          inline: 'nearest'
        });
        host.requestAnimationFrame(() => host.requestAnimationFrame(start));
      } else start();
    }

    async function update(next) {
      var _next$pack, _next$pack2;

      if (disposed) return;
      snapshot = next;

      if ((_next$pack = next.pack) === null || _next$pack === void 0 ? void 0 : _next$pack.outcome) {
        let result;

        try {
          var _result, _result2;

          result = await resolveOutcome(next.pack.outcome);
          if (disposed || snapshot !== next) return;
          P.check(((_result = result) === null || _result === void 0 ? void 0 : _result.style) && ((_result2 = result) === null || _result2 === void 0 ? void 0 : _result2.card) && Object.prototype.hasOwnProperty.call(ranks, result.card.effect), 'UNBOUND_PUBLIC_ARTWORK');
        } catch (e) {
          onError(e);
          return;
        }

        if (visual && visual.style.styleId !== result.style.styleId) {
          onError(Error('PACK_STYLE_CHANGED'));
          return;
        }

        if (!visual) {
          visual = result;
          assets.retain([{
            style: visual.style,
            size: 'card'
          }]);
          assetPromise = assets.ensure(visual.style, 'card');
        }

        if (!image) {
          const gen = assetGeneration,
                loaded = await assetPromise;
          if (disposed || gen !== assetGeneration) return;

          if (loaded.status !== 'ready') {
            onError(Error(loaded.error || 'ARTWORK_NOT_READY'));
            return;
          }

          image = loaded.image;
        }
      }

      if (((_next$pack2 = next.pack) === null || _next$pack2 === void 0 ? void 0 : _next$pack2.status) === 'OPENED' && image && !played) {
        played = true;
        gesture.setProgress(1);
        showEffect();
      }

      requestDraw();
    }

    async function begin() {
      var _snapshot5;

      if (pending || ((_snapshot5 = snapshot) === null || _snapshot5 === void 0 ? void 0 : _snapshot5.busy) || disposed) return false;
      pending = true;

      try {
        var _snapshot6, _snapshot6$pack, _snapshot7, _snapshot7$pack;

        if (((_snapshot6 = snapshot) === null || _snapshot6 === void 0 ? void 0 : (_snapshot6$pack = _snapshot6.pack) === null || _snapshot6$pack === void 0 ? void 0 : _snapshot6$pack.status) === 'SEALED') {
          const result = await onBegin();
          await update(result);
        }

        return !!image && ((_snapshot7 = snapshot) === null || _snapshot7 === void 0 ? void 0 : (_snapshot7$pack = _snapshot7.pack) === null || _snapshot7$pack === void 0 ? void 0 : _snapshot7$pack.status) === 'REVEALED';
      } catch (e) {
        onError(e);
        return false;
      } finally {
        pending = false;
      }
    }

    async function finish() {
      var _snapshot8, _snapshot9, _snapshot9$pack;

      if (pending || ((_snapshot8 = snapshot) === null || _snapshot8 === void 0 ? void 0 : _snapshot8.busy) || ((_snapshot9 = snapshot) === null || _snapshot9 === void 0 ? void 0 : (_snapshot9$pack = _snapshot9.pack) === null || _snapshot9$pack === void 0 ? void 0 : _snapshot9$pack.status) !== 'REVEALED' || !image) return;
      pending = true;

      try {
        await update((await onComplete()));
      } catch (e) {
        onError(e);
      } finally {
        pending = false;
        requestDraw();
      }
    }

    function down(e) {
      var _snapshot10, _snapshot11, _snapshot11$pack, _snapshot12, _snapshot12$pack;

      if (e.isPrimary === false || e.button > 0 || pending || ((_snapshot10 = snapshot) === null || _snapshot10 === void 0 ? void 0 : _snapshot10.busy)) return;
      const p = point(e);
      if (p.x < CARD.x - 20 || p.x > CARD.x + CARD.w + 20 || p.y < CARD.y - 20 || p.y > CARD.y + CARD.h + 20) return;

      if (((_snapshot11 = snapshot) === null || _snapshot11 === void 0 ? void 0 : (_snapshot11$pack = _snapshot11.pack) === null || _snapshot11$pack === void 0 ? void 0 : _snapshot11$pack.status) === 'SEALED') {
        const held = {
          id: e.pointerId,
          latest: p
        };
        contact = held;
        canvas.setPointerCapture(e.pointerId);
        e.preventDefault();
        begin().then(ready => {
          if (disposed || contact !== held || !ready) return;
          contact = null;

          if (gesture.begin(held.latest, held.id)) {
            pointer = held.id;
            requestDraw();
          }
        });
        return;
      }

      if (((_snapshot12 = snapshot) === null || _snapshot12 === void 0 ? void 0 : (_snapshot12$pack = _snapshot12.pack) === null || _snapshot12$pack === void 0 ? void 0 : _snapshot12$pack.status) !== 'REVEALED' || !image) return;

      if (gesture.begin(p, e.pointerId)) {
        pointer = e.pointerId;
        canvas.setPointerCapture(e.pointerId);
        e.preventDefault();
      }
    }

    function move(e) {
      var _contact, _snapshot13, _snapshot13$pack;

      const p = point(e);

      if (((_contact = contact) === null || _contact === void 0 ? void 0 : _contact.id) === e.pointerId) {
        contact.latest = p;
        return;
      }

      if (((_snapshot13 = snapshot) === null || _snapshot13 === void 0 ? void 0 : (_snapshot13$pack = _snapshot13.pack) === null || _snapshot13$pack === void 0 ? void 0 : _snapshot13$pack.status) === 'OPENED') {
        tiltX = Math.max(-1, Math.min(1, (p.x - 240) / 180));
        tiltY = Math.max(-1, Math.min(1, (p.y - 290) / 230));
        requestDraw();
        return;
      }

      if (pointer !== e.pointerId) return;
      const s = gesture.move(p, e.pointerId);
      audio.peel(s.progress);
      requestDraw();
    }

    function up(e) {
      var _contact2, _canvas$hasPointerCap2;

      if (((_contact2 = contact) === null || _contact2 === void 0 ? void 0 : _contact2.id) === e.pointerId) {
        var _canvas$hasPointerCap;

        contact = null;
        if ((_canvas$hasPointerCap = canvas.hasPointerCapture) === null || _canvas$hasPointerCap === void 0 ? void 0 : _canvas$hasPointerCap.call(canvas, e.pointerId)) canvas.releasePointerCapture(e.pointerId);
        return;
      }

      if (pointer !== e.pointerId) return;
      const s = gesture.end(e.pointerId);
      pointer = null;
      if ((_canvas$hasPointerCap2 = canvas.hasPointerCapture) === null || _canvas$hasPointerCap2 === void 0 ? void 0 : _canvas$hasPointerCap2.call(canvas, e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      if (s.released && s.complete) finish();
      requestDraw();
    }

    function cancel(e) {
      contact = null;
      gesture.cancel(e === null || e === void 0 ? void 0 : e.pointerId);
      pointer = null;
      requestDraw();
    }

    async function openExplicit() {
      var _snapshot14, _snapshot14$pack;

      if (((_snapshot14 = snapshot) === null || _snapshot14 === void 0 ? void 0 : (_snapshot14$pack = _snapshot14.pack) === null || _snapshot14$pack === void 0 ? void 0 : _snapshot14$pack.status) === 'OPENED') {
        showEffect();
        return;
      }

      if (await begin()) {
        gesture.setProgress(1);
        await finish();
      }
    }

    async function key(e) {
      var _snapshot15, _snapshot15$pack, _snapshot16, _snapshot16$pack;

      if (!['Enter', ' ', 'ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();

      if (e.key === 'Enter' || e.key === ' ') {
        await openExplicit();
        return;
      }

      if (((_snapshot15 = snapshot) === null || _snapshot15 === void 0 ? void 0 : (_snapshot15$pack = _snapshot15.pack) === null || _snapshot15$pack === void 0 ? void 0 : _snapshot15$pack.status) === 'SEALED') {
        await begin();
        return;
      }

      if (((_snapshot16 = snapshot) === null || _snapshot16 === void 0 ? void 0 : (_snapshot16$pack = _snapshot16.pack) === null || _snapshot16$pack === void 0 ? void 0 : _snapshot16$pack.status) === 'REVEALED' && image && !pending && !snapshot.busy) {
        const plus = ['ArrowUp', 'ArrowLeft'].includes(e.key),
              p = Math.max(0, Math.min(1, gesture.snapshot().progress + (plus ? .1 : -.1)));
        gesture.setProgress(p);
        audio.peel(p);
        requestDraw();
        if (p === 1) finish();
      }
    }

    function visibility() {
      if (doc.hidden) {
        cancel();
        fx.stop();
        if (frame) host.cancelAnimationFrame(frame);
        frame = 0;
      } else requestDraw();
    }

    function motionChange() {
      if (reduced()) fx.stop();
      requestDraw();
    }

    const events = [...(pointerSupported ? [['pointerdown', down], ['pointermove', move], ['pointerup', up], ['pointercancel', cancel], ['lostpointercapture', cancel]] : []), ['keydown', key]];

    for (const [name, fn] of events) canvas.addEventListener(name, fn);

    reduceControl.addEventListener('change', motionChange);
    if (motion.addEventListener) motion.addEventListener('change', motionChange);else motion.addListener(motionChange);
    doc.addEventListener('visibilitychange', visibility);
    host.addEventListener('resize', resize);
    (_host$visualViewport = host.visualViewport) === null || _host$visualViewport === void 0 ? void 0 : _host$visualViewport.addEventListener('resize', resize);
    resize();
    return {
      update,
      openExplicit,
      pointerSupported,

      async retryArtwork() {
        if (!visual) return;
        const loaded = await assets.retry(visual.style, 'card');
        if (disposed) return;

        if (loaded.status === 'ready') {
          image = loaded.image;
          requestDraw();
        } else onError(Error(loaded.error));
      },

      dispose() {
        var _host$visualViewport2;

        disposed = true;
        ++assetGeneration;

        for (const [name, fn] of events) canvas.removeEventListener(name, fn);

        reduceControl.removeEventListener('change', motionChange);
        if (motion.removeEventListener) motion.removeEventListener('change', motionChange);else motion.removeListener(motionChange);
        doc.removeEventListener('visibilitychange', visibility);
        host.removeEventListener('resize', resize);
        (_host$visualViewport2 = host.visualViewport) === null || _host$visualViewport2 === void 0 ? void 0 : _host$visualViewport2.removeEventListener('resize', resize);
        if (frame) host.cancelAnimationFrame(frame);
        fx.stop();
        audio.dispose();
        assets.dispose();
      }

    };
  }

  async function mountOwned({
    canvas,
    card,
    style,
    isCurrent = () => true,
    signal
  }) {
    await ensureVendors();
    P.check(card && style, 'OWNED_ARTWORK_REQUIRED');
    let disposed = false;
    const cache = host.OPCardAssets.create({
      maxEntries: 2,
      maxPixels: 8000000
    }),
          ctx = canvas.getContext('2d');

    function dispose() {
      disposed = true;
      signal === null || signal === void 0 ? void 0 : signal.removeEventListener('abort', dispose);
      cache.dispose();
      if (isCurrent()) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
      dispose();
      return {
        dispose
      };
    }

    signal === null || signal === void 0 ? void 0 : signal.addEventListener('abort', dispose, {
      once: true
    });
    cache.retain([{
      style,
      size: 'inspect'
    }]);
    const result = await cache.ensure(style, 'inspect');

    if (disposed || !isCurrent()) {
      dispose();
      return {
        dispose
      };
    }

    if (result.status !== 'ready') {
      dispose();
      throw Error(result.error);
    }

    host.OPCardRenderer.draw(ctx, { ...card,
      id: Number(card.cardId.slice(2))
    }, style, result.image, {
      x: 0,
      y: 0,
      w: canvas.width,
      h: canvas.height
    }, {
      glare: true
    });
    return {
      dispose
    };
  }

  return {
    mount,
    mountOwned
  };
}

module.exports = {
  createVisuals
};
